import { memo, useEffect } from 'react'
// eslint-disable-next-line import/no-named-default
import { default as NextError } from 'next/error'
import { useRouter } from 'next/router'
import { STATUS_CODES } from 'http'
import { CssBaseline, ThemeProvider, useMediaQuery } from '@mui/material'
import { Theme } from '@mui/material/styles'

import {
  RegisteredEventName,
  broadcastToParent,
} from '@finanzcheck/catalyst-pollard'

import BankListPage from '../../components/page/BankListPage'

import { LOADING_FETCH_STATUS_CODE, useBankList } from '../../hooks/useBankList'
import useUrlParams from '../../hooks/useUrlParams'
import { getRegRoutePath } from '../../utils/url'
import { isSmava } from '../../utils/getTenantInfo.utils'
import { initPollardCommunication } from '../../utils/pollard'
import { customizeTheme } from '../../utils/theme.utils'
import theme, { SupportedTheme } from '../../theme'
import { getSessionId } from '../../utils/session.utils'

const BankListEmbed = () => {
  const router = useRouter()
  const queryParams = useUrlParams()
  const regRoutePath = getRegRoutePath('/coop/embed')

  const {
    bankListContextData,
    currentBankList,
    fetchStatus,
    advertisementId,
    refId,
  } = useBankList({
    pathname: '/banklist/embed',
    regRoutePath,
    queryParams,
    router,
  })

  customizeTheme(SupportedTheme.Embedded, queryParams)

  const isMobileOrTablet = useMediaQuery<Theme>(theme.breakpoints.down('md'))

  useEffect(() => {
    initPollardCommunication({
      isAsyncData: true,
      elemRef: '#banklist-container',
      // The offset here is to cover the height of loan selector section
      // (the height of loan selector section is diferent on mobile/tablet and desktop)
      offset: isMobileOrTablet ? (isSmava ? 650 : 725) : 425,
    })
  })

  useEffect(() => {
    const fcId = getSessionId()

    broadcastToParent({
      eventName: RegisteredEventName.tracking,
      data: {
        event: '/affiliate:banklist',
        fcId,
        ...queryParams,
        advertisementId,
        refId,
      },
    })

    broadcastToParent({
      eventName: RegisteredEventName.tracking,
      data: {
        event: '/affiliate:visit',
        fcId,
        ...queryParams,
        advertisementId,
        refId,
        src: 'banklist',
      },
    })
  }, [advertisementId, refId])

  if (
    currentBankList &&
    (STATUS_CODES?.[fetchStatus] === 'OK' ||
      fetchStatus === LOADING_FETCH_STATUS_CODE)
  ) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <BankListPage
          hasHeader={false}
          extendedFooter={false}
          bankListContextData={bankListContextData}
          currentBankList={currentBankList}
          fetchStatus={fetchStatus}
        />
      </ThemeProvider>
    )
  }

  return <NextError statusCode={fetchStatus} />
}

export default memo(BankListEmbed)
